import { CustomerOrderBillingPlanLineView } from "src/api/generated/erp/db/types/tables/customerOrderBillingPlanLineView.ts";
import { CustomerOrderBillingPlanApi } from "src/api/generated/erp/sales/billingPlan/api/customerOrderBillingPlanApi.ts";
import { dataGridPersistentStateProps } from "src/components/common/dataGrid/dataGridStateStorage.ts";
import {
	booleanColumn,
	dateColumn,
	dateTimeColumn,
	floatColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { Box, Typography } from "@mui/material";
import { BillingPlanSummary } from "src/api/generated/erp/sales/billingPlan/service/billingPlanSummary.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faFileInvoice } from "@fortawesome/pro-regular-svg-icons";
import { useRowSelection } from "src/components/common/dataGrid/useRowSelection.ts";
import { useGenericDialog } from "src/components/common/dialogs/useGenericDialog.ts";
import { CustomerOrderInvoiceCreationForm } from "src/components/views/erp/sales/invoicing/customerOrderInvoicing/CustomerOrderInvoiceCreationForm.tsx";
import { InvoiceCreationApi } from "src/api/generated/erp/sales/invoicing/api/invoiceCreationApi.ts";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";

export interface BillingPlanInvoicingViewProps {
	customerOrderId: number;
	onInvoicesModified: () => void;
}

export const BillingPlanInvoicingView = ({
	customerOrderId,
	onInvoicesModified,
}: BillingPlanInvoicingViewProps) => {
	const { onlySelectedRow, setSelectedRows } =
		useRowSelection<CustomerOrderBillingPlanLineView>();
	const { openDialog } = useGenericDialog();

	const showInvoiceCreationForm = (refresh: () => Promise<unknown>) => {
		if (!onlySelectedRow) return;

		openDialog(({ closeDialog }) => ({
			title: i18n.t("create_invoice"),
			size: "sm",
			children: (
				<CustomerOrderInvoiceCreationForm
					customerOrderId={customerOrderId}
					showCustomerOrderLines={false}
					closeForm={closeDialog}
					submit={async ({ invoiceDate }) => {
						await InvoiceCreationApi.createInvoiceFromCustomerOrderBillingPlanLine({
							customerOrderBillingPlanLineId:
								onlySelectedRow.customerOrderBillingPlanLineId,
							invoiceDate: invoiceDate,
						});
						await refresh();
						onInvoicesModified();
					}}
				/>
			),
		}));
	};

	return (
		<AsyncFetchRender
			fetch={() =>
				CustomerOrderBillingPlanApi.getCustomerOrderBillingPlan({ customerOrderId })
			}
			content={({ lines, summary }, refresh) => (
				<AavoDataGrid<CustomerOrderBillingPlanLineView>
					rows={lines}
					onRefresh={refresh}
					disableMultipleRowSelection
					onRowSelectionChanged={setSelectedRows}
					getRowId={(row) => row.customerOrderBillingPlanLineId}
					getRowClassNames={({ row }) => {
						switch (row.billingPlanLineState) {
							case "INVOICED":
								return dataGridRowClassNames.green;
							case "APPROVED":
								return dataGridRowClassNames.yellow;
							default:
								return "";
						}
					}}
					columns={[
						textColumn({
							field: "description",
							headerName: i18n.t("description"),
							width: 200,
						}),
						floatColumn({
							field: "billingPlanSum",
							headerName: i18n.t("sum"),
						}),
						floatColumn({
							field: "billingPlanSumWithVat",
							headerName: i18n.t("sum_with_vat"),
							width: 140,
						}),
						floatColumn({
							field: "billingPlanPercent",
							headerName: i18n.t("share_percent"),
							width: 100,
						}),
						dateColumn({
							field: "estimatedApprovalDate",
							headerName: i18n.t("estimated_approval_date"),
							width: 170,
						}),
						dateColumn({
							field: "estimatedPaymentDate",
							headerName: i18n.t("estimated_payment_date"),
							width: 150,
						}),
						dateTimeColumn({
							field: "approvedAt",
							headerName: i18n.t("approved_at"),
							width: 150,
						}),
						textColumn({
							field: "approvedByUserName",
							headerName: i18n.t("approved_by"),
							width: 120,
						}),
						dateColumn({
							field: "invoiceDate",
							headerName: i18n.t("invoice_date"),
						}),
						textColumn({
							field: "invoicedByUserName",
							headerName: i18n.t("invoiced_by"),
						}),
						textColumn({
							field: "salesAccountingCode",
							headerName: i18n.t("accounting_code"),
						}),
						booleanColumn({
							field: "funded",
							headerName: i18n.t("funded"),
						}),
					]}
					actionBarComponents={
						<>
							<AavoButton
								icon={faFileInvoice}
								label={i18n.t("create_invoice")}
								disabled={() => {
									if (!onlySelectedRow) return i18n.t("select_a_row");
									switch (onlySelectedRow.billingPlanLineState) {
										case "INITIAL":
											return i18n.t("billing_plan_line_is_not_yet_approved");
										case "INVOICED":
											return i18n.t("billing_plan_line_is_already_invoiced");
										default:
											return false;
									}
								}}
								variant={"outlined"}
								onClick={() => showInvoiceCreationForm(refresh)}
							/>
							<OpenCustomerOrderButton
								key={"openCustomerOrder"}
								customerOrderId={customerOrderId}
							/>
							<SummaryText summary={summary} />
						</>
					}
					{...dataGridPersistentStateProps("E9E5FA35C483F3C5")}
				/>
			)}
		/>
	);
};

const SummaryText = ({ summary }: { summary: BillingPlanSummary }) => {
	const fields = [
		{
			label: i18n.t("sum"),
			value: i18n.t("money", { val: summary.billingPlanSum }),
		},
		{
			label: i18n.t("sum_with_vat"),
			value: i18n.t("money", { val: summary.billingPlanSumWithVat }),
		},
		{
			label: i18n.t("share_of_price"),
			value: i18n.t("percent", { val: summary.percentOfTotalPrice }),
		},
	];

	return (
		<Box
			sx={{
				display: "flex",
				gap: 2,
				alignSelf: "center",
			}}
		>
			{fields.map(({ label, value }) => (
				<Typography key={label}>
					<i>{label}</i>: {value}
				</Typography>
			))}
		</Box>
	);
};
